import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import MagnetLinkManager from './MagnetLinkManager';
import DiscussionTooltip from './DiscussionTooltip';
import addTextEditorButton from './addTextEditorButton';

// Ustawienia wyglądu karty (zmienne CSS + klasa stylu mobilnego), czytane z
// forum-atrybutów. UWAGA: app.forum NIE jest dostępne w momencie uruchamiania
// initializera, więc stosujemy to leniwie — przy pierwszej inicjalizacji
// magnetu (gdy app.forum jest już dostępne). Flaga gwarantuje jednokrotność.
let magnetDisplaySettingsApplied = false;
function applyMagnetDisplaySettings() {
  if (magnetDisplaySettingsApplied || !app.forum) return;
  magnetDisplaySettingsApplied = true;
  const root = document.documentElement;
  const maxLines = parseInt(app.forum.attribute('magnetNameMaxLines'), 10);
  root.style.setProperty('--magnet-name-max-lines', String(Number.isFinite(maxLines) && maxLines > 0 ? maxLines : 3));
  const allowedJustify = ['space-between', 'space-around', 'center', 'flex-start'];
  const justify = app.forum.attribute('magnetStatsJustify');
  root.style.setProperty('--magnet-stats-justify', allowedJustify.includes(justify) ? justify : 'space-between');
  if (document.body) {
    document.body.classList.toggle('magnet-mobile-style', app.forum.attribute('magnetDesktopStyle') === 'mobile');
  }
}
app.initializers.add('tryhackx-magnet-link', () => {
  // Inicjalizacja managera magnet linków
  app.magnetLinkManager = new MagnetLinkManager();

  // Dodaj przycisk BBCode do edytora
  addTextEditorButton();

  // Extend CommentPost to initialize magnet links after render
  extend(CommentPost.prototype, 'oncreate', function () {
    this.initMagnetLinks();
  });
  extend(CommentPost.prototype, 'onupdate', function () {
    this.initMagnetLinks();
  });
  CommentPost.prototype.initMagnetLinks = function () {
    applyMagnetDisplaySettings();
    const element = this.element;
    if (!element) return;

    // Szukamy elementów z data-token (nie data-magnet!)
    const magnetElements = element.querySelectorAll('.MagnetLink:not([data-initialized])');
    magnetElements.forEach(el => {
      el.setAttribute('data-initialized', 'true');
      const token = el.getAttribute('data-token');

      // Sprawdź czy token jest prawidłowy (64 znaki hex)
      if (token && token !== 'invalid' && token !== '' && /^[a-f0-9]{64}$/i.test(token)) {
        var _this$attrs, _this$attrs$id, _this$attrs2, _this$attrs2$user, _this$attrs2$user$id, _this$attrs3;
        const postId = ((_this$attrs = this.attrs) == null || (_this$attrs = _this$attrs.post) == null || (_this$attrs$id = _this$attrs.id) == null ? void 0 : _this$attrs$id.call(_this$attrs)) || null;
        const postUserId = ((_this$attrs2 = this.attrs) == null || (_this$attrs2 = _this$attrs2.post) == null || (_this$attrs2$user = _this$attrs2.user) == null || (_this$attrs2$user = _this$attrs2$user.call(_this$attrs2)) == null || (_this$attrs2$user$id = _this$attrs2$user.id) == null ? void 0 : _this$attrs2$user$id.call(_this$attrs2$user)) || ((_this$attrs3 = this.attrs) == null || (_this$attrs3 = _this$attrs3.post) == null || (_this$attrs3 = _this$attrs3.data) == null || (_this$attrs3 = _this$attrs3.relationships) == null || (_this$attrs3 = _this$attrs3.user) == null || (_this$attrs3 = _this$attrs3.data) == null ? void 0 : _this$attrs3.id) || null;
        app.magnetLinkManager.initializeMagnetLink(el, token, postId, postUserId);
      } else {
        // Invalid lub pusty token
        el.innerHTML = '<div class="MagnetLink-container MagnetLink-error">' + '<span class="MagnetLink-icon"><i class="fas fa-exclamation-triangle"></i></span>' + '<span class="MagnetLink-text">Invalid magnet link</span>' + '</div>';
      }
    });
  };

  // Tooltip na liście dyskusji (lazy init - app.forum niedostępne przy inicjalizacji)
  extend(DiscussionListItem.prototype, 'oncreate', function () {
    if (!app.forum.attribute('magnetTooltipEnabled')) return;
    if (!app.magnetTooltip) app.magnetTooltip = new DiscussionTooltip();
    this.setupMagnetTooltip(app.magnetTooltip);
  });
  extend(DiscussionListItem.prototype, 'onupdate', function () {
    if (!app.forum.attribute('magnetTooltipEnabled')) return;
    if (!app.magnetTooltip) app.magnetTooltip = new DiscussionTooltip();
    this.setupMagnetTooltip(app.magnetTooltip);
  });
  DiscussionListItem.prototype.setupMagnetTooltip = function (tooltip) {
    const element = this.element;
    if (!element || element.hasAttribute('data-magnet-tooltip')) return;

    // Skip discussions whose opening post has no magnet link — avoids a
    // pointless /magnet/discussion request (and loading flash) on hover.
    if (!this.attrs.discussion || !this.attrs.discussion.attribute('hasMagnetLinks')) return;
    element.setAttribute('data-magnet-tooltip', 'true');
    const mainArea = element.querySelector('.DiscussionListItem-main');
    if (!mainArea) return;
    const discussionId = this.attrs.discussion.id();
    let hoverTimeout;
    mainArea.addEventListener('mouseenter', () => {
      hoverTimeout = setTimeout(() => {
        tooltip.show(discussionId, mainArea);
      }, 300);
    });
    mainArea.addEventListener('mouseleave', () => {
      clearTimeout(hoverTimeout);
      tooltip.hide();
      // Zwolnij workera: przerwij żądanie scrapingu tego tematu w locie
      // (metoda B — abort przy opuszczeniu / zmianie najechanego tematu).
      tooltip.cancel(discussionId);
    });
  };

  // Obserwuj zmiany DOM dla dynamicznie ładowanych postów
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            var _node$classList, _node$querySelectorAl;
            // Tani odcień przed pełnym skanem poddrzewa: obserwator jest
            // podpięty pod document.body (subtree:true), więc callback biegnie
            // na KAŻDY dodany węzeł w całym SPA (audyt: narzut przy dużym
            // churnie DOM). Węzeł-liść, który nie jest sam magnetem, nie ma
            // czego skanować — pomijamy, zanim ruszymy querySelectorAll.
            // (Świadomie NIE przepinamy obserwatora na .App-content: magnety
            // renderują się też w podglądzie kompozytora / modalach poza nim.)
            if (!node.childElementCount && !((_node$classList = node.classList) != null && _node$classList.contains('MagnetLink'))) return;
            const magnetElements = ((_node$querySelectorAl = node.querySelectorAll) == null ? void 0 : _node$querySelectorAl.call(node, '.MagnetLink:not([data-initialized])')) || [];
            if (magnetElements.length) applyMagnetDisplaySettings();
            magnetElements.forEach(el => {
              el.setAttribute('data-initialized', 'true');
              const token = el.getAttribute('data-token');
              if (token && token !== 'invalid' && token !== '' && /^[a-f0-9]{64}$/i.test(token)) {
                app.magnetLinkManager.initializeMagnetLink(el, token, null, null);
              } else {
                el.innerHTML = '<div class="MagnetLink-container MagnetLink-error">' + '<span class="MagnetLink-icon"><i class="fas fa-exclamation-triangle"></i></span>' + '<span class="MagnetLink-text">Invalid magnet link</span>' + '</div>';
              }
            });
          }
        });
      });
    });

    // Obserwuj body dla dynamicznych zmian
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }
});